import {
  FormField,
  Input,
  NonCancelableCustomEvent,
  Textarea,
  TextareaProps,
} from '@amzn/awsui-components-core';
import React from 'react';
import constants from '../../../common/constants';
import InfoLink from '../../common/InfoLink';

interface Props {
  onInputUpdated?: (sectionId: string, value: string, validationError?: string) => void;
  onValidationError?: (validationError?: string) => void;
  sectionId?: string;
  readonly?: boolean;
  value: string;
  label: string;
  valid?: boolean;
  description?: string;
  contextualHelp?: string;
  placeholder?: string;
  isRequired?: boolean;
  stretch?: boolean;
  showValidationError?: boolean;
  disabled?: boolean;
  textarea?: boolean;
  rows?: number;
  visible?: boolean;
  minLength?: number;
  maxLength?: number;
}

interface State {
  validationError?: string;
  value: string;
}

class MetadataStringInput extends React.Component<Props, State> {
  private static defaultProps = {
    description: '',
    disabled: false,
    isRequired: false,
    placeholder: '',
    readonly: false,
    showValidationError: true,
    stretch: false,
    textarea: false,
    rows: 0,
    valid: true,
    visible: true,
    minLength: 0,
    maxLength: 300,
  };

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    const value = props.value;
    const validationError = this.validateInput(value);
    this.state = {
      validationError,
      value,
    };
  }

  public componentDidUpdate(prevProps: Props, prevState: State) {
    if (this.props.value !== prevProps.value && this.props.value !== this.state.value) {
      this.setState({
        value: this.props.value,
      });
      this.validateInput(this.props.value);
    }

    if (prevProps.showValidationError && !this.props.showValidationError) {
      this.setState({
        validationError: undefined,
      });
    }
  }

  public componentDidMount() {
    const { value } = this.state;
    this.validateInput(value);
  }

  public render() {
    const {
      readonly,
      label,
      description,
      contextualHelp,
      placeholder,
      stretch,
      isRequired,
      showValidationError,
      disabled,
      textarea,
      rows,
      visible,
      valid,
    } = this.props;
    const { value, validationError } = this.state;
    if (!visible) {
      return null;
    } else if (readonly) {
      return (
        <details open={!!value}>
          <summary>{label}</summary>
          <p>{value}</p>
        </details>
      );
    } else {
      const inputControl = textarea ? (
        <Textarea
          onChange={this.handleInput}
          value={value}
          invalid={!valid}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          ariaRequired={isRequired}
        />
      ) : (
        <Input
          onChange={this.handleInput}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={false}
          ariaRequired={isRequired}
        />
      );
      return (
        <FormField
          errorText={showValidationError ? validationError : ''}
          label={
            <span>
              {label}
              {!isRequired && <i> - optional</i>}
            </span>
          }
          info={contextualHelp && <InfoLink topic={contextualHelp} />}
          description={description}
          stretch={stretch}
          i18nStrings={{ errorIconAriaLabel: 'Error' }}
        >
          {inputControl}
        </FormField>
      );
    }
  }

  private handleInput = (e: NonCancelableCustomEvent<TextareaProps.ChangeDetail>) => {
    const value: string = e.detail.value.trim();
    const error = this.validateInput(value);

    this.setState({
      validationError: error,
      value,
    });

    const { sectionId, onInputUpdated: onUpdated } = this.props;

    if (sectionId && onUpdated) {
      onUpdated(sectionId, value, error);
    }
  };

  private validateInput = (value: string) => {
    const { isRequired, minLength, maxLength, onValidationError } = this.props;

    let error: string;

    // @ts-expect-error TS18048 - strict migration
    if (value && (value.length > maxLength || value.length < minLength)) {
      error = `Must be between ${minLength} and ${maxLength} characters.`;
    }

    if (isRequired && !value) {
      error = constants.REQUIRED;
    }

    if (onValidationError) {
      // @ts-expect-error TS2454 - strict migration
      onValidationError(error);
    }

    // @ts-expect-error TS2454 - strict migration
    return error;
  };
}

export default MetadataStringInput;
